import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Tag, Button, Space, InputNumber, Select, Descriptions, Modal, Typography, message, Tooltip } from 'antd';
import { ReloadOutlined, SendOutlined, LinkOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import apiClient from '../../../shared/services/apiClient';
import AdminNotices from './AdminNotices';
import MarketingFlows from './MarketingFlows';

const { Text } = Typography;
const call = async (p) => {
  try {
    const r = await p;
    return r.data?.data;
  } catch (e) {
    throw e.response?.data || e;
  }
};
const api = {
  settings: () => call(apiClient.get('/api/admin/notifications/settings')),
  opsBindCode: () => call(apiClient.post('/api/admin/notifications/ops-bind-code')),
  test: (user_id, channel) => call(apiClient.post('/api/admin/notifications/test', { user_id, channel })),
  deliveries: (params) => call(apiClient.get('/api/admin/notifications/deliveries', { params })),
};

const STATUS = { sent: 'green', failed: 'red', pending: 'gold', sending: 'blue', skipped: 'default' };
const LEVEL = { critical: 'red', warning: 'orange', info: 'blue' };

/**
 * 用户通知（与运维告警共用 Telegram 机器人；Token 在运维告警页配置）。
 * 机器人状态 / 运维对话绑定码 / 给用户发测试 / 最近投递记录。
 */
export default function UserNotifications() {
  const [info, setInfo] = useState(null);
  const [rows, setRows] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState({ page: 1, page_size: 20 });
  const [loading, setLoading] = useState(false);
  const [testUser, setTestUser] = useState(null);
  const [testCh, setTestCh] = useState('all');
  const [bind, setBind] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([api.settings(), api.deliveries(filters)]);
      setInfo(s);
      setRows(d || { items: [], total: 0 });
    } catch (e) {
      message.error(e.error || e.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const genOpsCode = async () => {
    try {
      setBind(await api.opsBindCode());
    } catch (e) {
      message.error(e.error || '生成失败');
    }
  };

  const sendTest = async () => {
    if (!testUser) return;
    try {
      const r = await api.test(testUser, testCh);
      message.success(`已发送到 ${r.targets} 个渠道`);
      setTimeout(load, 3000);
    } catch (e) {
      message.error(e.error || '发送失败');
    }
  };

  const bot = info?.bot || {};
  const columns = [
    { title: '时间', dataIndex: 'created_at', width: 160 },
    { title: '用户', dataIndex: 'username', width: 120, render: (v, r) => `${v || ''} #${r.user_id}` },
    { title: '分类', dataIndex: 'category', width: 120, render: (v, r) => <Tag color={LEVEL[r.level]}>{v}</Tag> },
    { title: '标题', dataIndex: 'title', ellipsis: true },
    { title: '渠道', dataIndex: 'channel', width: 90 },
    { title: '目标', dataIndex: 'target', width: 180, ellipsis: true },
    { title: '状态', dataIndex: 'status', width: 90, render: (v, r) => <Tooltip title={r.error}><Tag color={STATUS[v]}>{v}</Tag></Tooltip> },
    { title: '次数', dataIndex: 'attempts', width: 60 },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        size="small"
        title="用户通知 · Telegram 机器人"
        extra={<Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />}
      >
        {info && !info.ready && <Text type="warning">通知表未建（待执行迁移 scripts/apply_user_notifications_migration.php）</Text>}
        <Descriptions size="small" column={{ xs: 1, md: 3 }}>
          <Descriptions.Item label="机器人">
            {bot.configured
              ? (bot.username ? <a href={`https://t.me/${bot.username}`} target="_blank" rel="noopener noreferrer">@{bot.username}</a> : <Text type="danger">Token 无效（getMe 失败）</Text>)
              : <Text type="secondary">未配置（在运维告警填 Token）</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="运维告警对话">
            <Space>
              {bot.ops_chat_bound ? <Tag color="green">已绑定</Tag> : <Tag>未绑定</Tag>}
              <Tooltip title="生成一次性绑定码（10 分钟），在目标对话里发送">
                <Button size="small" icon={<LinkOutlined />} disabled={!bot.username} onClick={genOpsCode}>绑定码</Button>
              </Tooltip>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="已绑 TG 用户">{info?.stats?.telegram_bound_users ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="24h 投递">
            {info?.stats ? Object.entries(info.stats.deliveries_24h || {}).map(([k, v]) => <Tag key={k} color={STATUS[k]}>{k} {v}</Tag>) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="测试发送" span={2}>
            <Space wrap>
              <InputNumber size="small" placeholder="用户 ID" min={1} value={testUser} onChange={setTestUser} style={{ width: 110 }} />
              <Select size="small" value={testCh} onChange={setTestCh} style={{ width: 110 }}
                options={[{ value: 'all', label: '全部渠道' }, { value: 'email', label: '邮件' }, { value: 'telegram', label: 'Telegram' }]} />
              <Button size="small" icon={<SendOutlined />} disabled={!testUser || !info?.ready} onClick={sendTest}>发送</Button>
            </Space>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <AdminNotices />

      <MarketingFlows />

      <Card size="small" title="最近投递"
        extra={(
          <Space>
            <Select size="small" allowClear placeholder="状态" style={{ width: 100 }} value={filters.status}
              onChange={(v) => setFilters((f) => ({ ...f, status: v, page: 1 }))}
              options={Object.keys(STATUS).map((k) => ({ value: k, label: k }))} />
            <Select size="small" allowClear placeholder="渠道" style={{ width: 100 }} value={filters.channel}
              onChange={(v) => setFilters((f) => ({ ...f, channel: v, page: 1 }))}
              options={[{ value: 'email', label: 'email' }, { value: 'telegram', label: 'telegram' }]} />
          </Space>
        )}
      >
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows.items}
          scroll={{ x: 900 }}
          pagination={{ current: filters.page, pageSize: filters.page_size, total: rows.total, showSizeChanger: false, onChange: (p) => setFilters((f) => ({ ...f, page: p })) }}
        />
      </Card>

      <Modal open={!!bind} onCancel={() => setBind(null)} footer={null} title="绑定运维告警对话" width={360}>
        {bind && (
          <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }}>
            {bind.deep_link && <div style={{ background: '#fff', padding: 10, display: 'inline-block' }}><QRCodeSVG value={bind.deep_link} size={160} /></div>}
            <Space wrap style={{ justifyContent: 'center' }}>
              {bind.deep_link && <Button type="primary" href={bind.deep_link} target="_blank">私聊绑定</Button>}
              {bind.deep_link_group && <Button href={bind.deep_link_group} target="_blank">加到群并绑定</Button>}
            </Space>
            <Text copyable={{ text: `/start ${bind.code}` }} code>/start {bind.code}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>单次有效，{bind.expires_at} 前使用；会替换当前告警对话</Text>
          </Space>
        )}
      </Modal>
    </Space>
  );
}
